import { Alert, VFlow } from 'bold-ui'
import { useAccordionControl } from 'components/accordion/useAccordionControl'
import { useFlags } from 'config/useFlagsContext'
import { FormApi } from 'final-form'
import { useAcessoHasCbosDisponiveisVinculacaoEquipeQuery } from 'graphql/hooks.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useServerTime } from 'hooks/useServerTime'
import React from 'react'
import { Route, useHistory, useRouteMatch } from 'react-router-dom'
import { emptyArray } from 'util/array'
import { MetaPath } from 'util/metaPath'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'

import { CompartilhamentoCuidadoTable } from '../compartilhamento-cuidado/compartilhamento-cuidado-table/CompartilhamentoCuidadoTable'
import { AdicionarCuidadoCompartilhadoForm } from './cuidado-compartilhado-form/AdicionarCuidadoCompartilhadoForm'
import { EditarCuidadoCompartilhadoModalForm } from './cuidado-compartilhado-form/EditarCuidadoCompartilhadoModalForm'
import { HistoricoEncaminhamentoExterno } from './cuidado-compartilhado-form/HistoricoEncaminhamentoExterno'
import { CuidadoCompartilhadoTable } from './cuidado-compartilhado-table/CuidadoCompartilhadoTable'
import { CuidadoCompartilhadoPlanoModel, EDITAR_CUIDADO_COMPARTILHADO_URL } from './model-cuidadocompartilhado'

interface CuidadoCompartilhadoViewProps {
  name: MetaPath<CuidadoCompartilhadoPlanoModel[]>
  dataReferencia: Instant
  isAtendimentoOdonto?: boolean
  isAtendimentoObservacao: boolean
}

export const CuidadoCompartilhadoView = (props: CuidadoCompartilhadoViewProps) => {
  const { name, dataReferencia, isAtendimentoOdonto = false, isAtendimentoObservacao } = props

  const history = useHistory()
  const match = useRouteMatch()
  const { analytics } = useFirebase()
  const { PILOTO_VINCULACAO_CUIDADO_ENABLED } = useFlags()
  const { getServerTimeNow } = useServerTime()
  const {
    data: { acessoHasCbosDisponiveisVinculacaoEquipe },
  } = useAcessoHasCbosDisponiveisVinculacaoEquipeQuery({
    skip: PILOTO_VINCULACAO_CUIDADO_ENABLED && !isAtendimentoOdonto,
  })

  const accordionProps = useAccordionControl({})

  const {
    id: atendimentoId,
    prontuario: { id: prontuarioId },
    atendimentoProfissional: { id: atendimentoProfissionalId, lotacao, iniciadoEm },
  } = useAtendimentoContext()

  const {
    handleSubmit,
    handleRowChanged,
    removeItem,
    input: { value = emptyArray },
  } = useEditableListField({ name })

  const onSubmit = (values: CuidadoCompartilhadoPlanoModel, form: FormApi<CuidadoCompartilhadoPlanoModel>) => {
    handleSubmit({ ...values, lastUpdate: getServerTimeNow().valueOf() }, form)
  }

  const onRowChanged = (values: CuidadoCompartilhadoPlanoModel) => {
    handleRowChanged({ ...values, lastUpdate: getServerTimeNow().valueOf() })
  }

  const handleEdit = (id: ID) => {
    history.push(`${match.url}${EDITAR_CUIDADO_COMPARTILHADO_URL}/${id}`)
  }

  const handleDelete = (id: ID) => {
    removeItem(value.find(({ _id: valueId }) => valueId === id))
    analytics.logEvent('CC_excluido')
  }

  return (
    <VFlow>
      {(PILOTO_VINCULACAO_CUIDADO_ENABLED && !isAtendimentoOdonto) || acessoHasCbosDisponiveisVinculacaoEquipe ? (
        <AdicionarCuidadoCompartilhadoForm
          onSubmit={onSubmit}
          lotacaoAtual={lotacao}
          isAtendimentoOdonto={isAtendimentoOdonto}
        />
      ) : (
        <Alert type='info'>
          Não é possível iniciar um compartilhamento de cuidado, pois não existem equipes NASF/eMulti vinculadas à
          equipe da sua lotação.
        </Alert>
      )}

      {PILOTO_VINCULACAO_CUIDADO_ENABLED ? (
        <CompartilhamentoCuidadoTable
          prontuarioId={prontuarioId}
          atendimentoId={atendimentoId}
          atendimentoProfissionalId={atendimentoProfissionalId}
          compartilhamentosAtendimentoAtual={value}
          iniciadoEm={iniciadoEm}
          lotacaoAtual={lotacao}
          isAtendimentoObservacao={isAtendimentoObservacao}
          onEdit={handleEdit}
          onDelete={handleDelete}
          {...accordionProps}
        />
      ) : (
        <CuidadoCompartilhadoTable
          prontuarioId={prontuarioId}
          cuidadosAtendimentoAtual={value}
          lotacaoAtual={lotacao}
          dataInicioAtendimento={iniciadoEm.getTime()}
          onEdit={handleEdit}
          onDelete={handleDelete}
          {...accordionProps}
        />
      )}

      {PILOTO_VINCULACAO_CUIDADO_ENABLED && !isAtendimentoOdonto && (
        <HistoricoEncaminhamentoExterno
          prontuarioId={prontuarioId}
          atendimentoId={atendimentoId}
          atendimentoProfissionalId={atendimentoProfissionalId}
          lotacao={lotacao}
          dataReferencia={dataReferencia}
        />
      )}

      <Route path={`${match.url}${EDITAR_CUIDADO_COMPARTILHADO_URL}/:id`}>
        <EditarCuidadoCompartilhadoModalForm
          compartilhamentosCuidado={value}
          onSubmit={onRowChanged}
          isAtendimentoOdonto={isAtendimentoOdonto}
        />
      </Route>
    </VFlow>
  )
}
